import { useState } from 'react'
import { Input, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DataTable, Loader } from '@kolab/dms-base/components/ui'
import { PageLayout } from '@kolab/dms-base/components/layout'
import { listPings } from '../../api/device.api'
import { pingKeys } from '../../api/queryKeys'
import type { Ping } from '../../api/device.types'
import { useMonolithCursorList } from '../../hooks/useMonolithCursorList'

const { Search } = Input

export default function PingHistoryPage() {
  const [search, setSearch] = useState('')

  const { items: pings, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useMonolithCursorList<Ping, Awaited<ReturnType<typeof listPings>>>({
      // Include search in the key so a search change refetches page 1.
      queryKey: pingKeys.list({ search }),
      queryFn: ({ cursor, limit }) =>
        listPings({ cursor, limit, search: search || undefined }),
      // /v1/pings returns { data, pagination: { hasMore, nextCursor } }.
      resolve: (raw) => ({
        items: raw.data,
        nextCursor: raw.pagination.nextCursor,
        hasMore: raw.pagination.hasMore,
      }),
      pageSize: 50,
    })

  const columns: ColumnsType<Ping> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    {
      title: 'Device',
      dataIndex: 'deviceId',
      key: 'deviceId',
      render: (id: string) => <a href={`/device-ops/${id}`}>{id}</a>,
    },
    {
      title: 'Time',
      dataIndex: 'pingAt',
      key: 'pingAt',
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: 'IP Address',
      key: 'ip',
      render: (_, r) => {
        const status = r.pingStatus as Record<string, unknown> | undefined
        return (status?.ipAddress as string) || '-'
      },
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, r) => {
        const status = r.pingStatus as Record<string, unknown> | undefined
        const geo = status?.geolocation as Record<string, unknown> | undefined
        if (!geo?.city) return '-'
        return `${geo.city}, ${geo.country}`
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, r) => {
        const status = r.pingStatus as Record<string, unknown> | undefined
        const s = status?.status as string | undefined
        if (!s) return '-'
        return <Tag color={s === 'success' ? 'green' : 'default'}>{s}</Tag>
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => new Date(v).toLocaleString(),
    },
  ]

  return (
    <PageLayout
      filters={
        <Search
          placeholder="Search by device..."
          allowClear
          onSearch={(v) => setSearch(v)}
          style={{ width: 280 }}
        />
      }
    >
      {isLoading ? (
        <Loader />
      ) : (
        <DataTable<Ping>
          rowKey="id"
          columns={columns}
          dataSource={pings}
          pagination={false}
          infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
        />
      )}
    </PageLayout>
  )
}
